import { nanoid } from 'nanoid';
import type { ElliottSections } from '../../../../pages/new-designs/tabs/aimd-v2/types';

export type FeedbackQuestionType = 'text' | 'list' | 'rating' | 'yesno' | 'roles';

const FEEDBACK_TYPE_LABELS: Record<FeedbackQuestionType, string> = {
  text: 'Text',
  list: 'List of Industries',
  rating: '1-5 Rating',
  yesno: 'Yes or No',
  roles: 'List of Roles'
};

const FEEDBACK_TYPE_ANSWER_FORMAT: Record<FeedbackQuestionType, string> = {
  text: 'Open input field',
  list: 'List of industries',
  rating: '1-5 rating',
  yesno: 'Yes, No',
  roles: 'List of roles'
};

export const mapFeedbackTypeToAnswerFormat = (type: FeedbackQuestionType): string =>
  FEEDBACK_TYPE_ANSWER_FORMAT[type] || FEEDBACK_TYPE_ANSWER_FORMAT.text;

export const mapAnswerFormatToFeedbackType = (value?: string): FeedbackQuestionType => {
  const normalized = (value || '').toLowerCase();
  if (normalized.includes('role')) return 'roles';
  if (normalized.includes('rating') || normalized.includes('1-5')) return 'rating';
  if (normalized.includes('yes')) return 'yesno';
  if (normalized.includes('list')) return 'list';
  return 'text';
};

export const getFeedbackTypeLabel = (type: FeedbackQuestionType): string =>
  FEEDBACK_TYPE_LABELS[type] ?? FEEDBACK_TYPE_LABELS.text;

export interface LessonMaterial {
  id: string;
  title: string;
  reference: string;
  thumbnail?: string;
  articleId?: string;
  readTime?: string;
  source?: 'ai' | 'library' | 'custom';
}

export interface FeedbackQuestion {
  id: string;
  _id?: string;
  prompt: string;
  type: FeedbackQuestionType;
}

export interface MultipleChoiceOption {
  id: string;
  label: string;
}

export interface MultipleChoiceQuestion {
  id: string;
  _id?: string;
  prompt: string;
  options: MultipleChoiceOption[];
  correctOptionId?: string;
  /** One rationale per option (aligned with `options` by index) — why each is right/wrong. */
  optionReasonings?: string[];
}

export interface Lesson {
  id: string;
  sourceId?: string;
  title: string;
  description: string;
  materials: LessonMaterial[];
  feedbackQuestions: FeedbackQuestion[];
  multipleChoiceQuestions: MultipleChoiceQuestion[];
  status?: string;
  usedRag?: boolean;
  generatedAt?: string | null;
  // Elliott sections stored on the live template task (task.elliott). Quiz is
  // NOT here — on the live layer it is materialized into task.MCQ.
  elliott?: Partial<ElliottSections> | null;
  /** AI-generation provenance carried on the live template task (PRD §9.3).
   *  Absent on never-improved manual lessons — the design editor renders its
   *  AI Reasoning CTA disabled until an Improve with AI run populates it. */
  reasoning?: string;
  learningOutcomesServed?: string[];
  ragDocumentTitles?: string[];
  /** The capability the lesson builds, by name, as the live template task carries it (task.capabilityName). */
  capabilityName?: string | null;
  /**
   * A Capability build-flow lesson's tags, as the task carries them: the
   * capability it builds or the outcomes it serves, and whether it has a
   * Scored Roleplay on (docs/PRD-capability-lesson-roleplays.md R8). Absent
   * on a lesson of a program built another way.
   */
  capabilityId?: string | null;
  outcomes?: string[];
  roleplay?: boolean;
  /**
   * Set on a lesson an Undo put back: its next save sends its Elliott sections
   * too, since the saved draft no longer has them (a save normally leaves them
   * to the server).
   */
  resendElliott?: boolean;
}

export interface ModuleDraft {
  id: string;
  sourceId?: string;
  title: string;
  objective?: string;
  lessons: Lesson[];
  status?: string;
  completedLessonsCount?: number;
  totalLessonsCount?: number;
  startDate?: { type: string; value: number };
  endDate?: { type: string; value: number };
}

export const createLesson = (): Lesson => ({
  id: nanoid(),
  title: 'New Lesson',
  description: '',
  materials: [],
  feedbackQuestions: [],
  multipleChoiceQuestions: []
});

export const createModule = (index: number): ModuleDraft => ({
  id: nanoid(),
  title: `Module ${index}`,
  objective: '',
  lessons: [createLesson()]
});
